import React, { useRef } from "react";
import { CheckIcon } from "icons/outline";
import { Button, Dialog, type ButtonProps } from "ui";

type ConfirmModalProps = {
  title: React.ReactNode;
  icon?: React.ReactNode;
  desc: React.ReactNode;
  extraContent?: React.ReactNode;
  confirmBtnText: React.ReactNode;
  cancelBtnText?: React.ReactNode;
  onConfirm: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onCancel?: () => void;
  open: boolean;
  onClose: () => void;
  confirmBtnProps?: Omit<ButtonProps, "className" | "onClick">;
};

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  title,
  icon,
  desc,
  extraContent,
  confirmBtnText,
  cancelBtnText = "Cancel",
  onConfirm,
  onCancel,
  open,
  onClose,
  confirmBtnProps,
}) => {
  const cancelButtonRef = useRef(null);

  return (
    <Dialog
      open={open}
      onOpenChange={() => {
        if (onCancel) {
          onCancel();
        }
        onClose();
      }}
    >
      <Dialog.Popup className="px-4 pb-4 pt-5 sm:p-6">
        <div>
          {icon ?? (
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-success-subtler">
              <CheckIcon className="h-6 w-6 text-success" aria-hidden="true" />
            </div>
          )}
          <div className="mt-3 text-center sm:mt-5">
            <Dialog.Title className="break-all">{title}</Dialog.Title>
            <Dialog.Description className="mt-2">{desc}</Dialog.Description>
            {extraContent}
          </div>
        </div>

        <Dialog.Footer variant="bare" className="mt-5 sm:mt-6">
          <Button
            {...confirmBtnProps}
            className="flex-1"
            onClick={(e) => {
              onConfirm(e);
              onClose();
            }}
          >
            {confirmBtnText}
          </Button>

          <Button
            variant="secondary"
            className="flex-1"
            onClick={() => {
              if (onCancel) {
                onCancel();
              }
              onClose();
            }}
            ref={cancelButtonRef}
          >
            {cancelBtnText}
          </Button>
        </Dialog.Footer>
      </Dialog.Popup>
    </Dialog>
  );
};

export default ConfirmModal;
